// The BLOCKED card: the SOCIAL section of the hub SETTINGS panel and of the
// ☸ OPTIONS menu of the game (deployment.md §7.6; card 17 of mockup round
// 081, R1731).
//
// What it shows: the accounts on the player's block list, newest first,
// each with LIFT, and the count against the cap. BLOCK beside a chat line
// adds an account: on the CLAN page, and on every chat tab of the game,
// the WORLD, FACTION and SQUAD channels included (R1328). This card is
// the one place a block is lifted. The list is the account's own and
// lives on the meta server, so it follows the player to any browser and
// survives a reload.
//
// The clan chat socket and every world drop a blocked account's lines for
// this reader alone. A lift reaches the clan chat at once and a world at
// the next join. Nobody else learns of a block, the blocked account
// included.
//
// **Two hosts mount the card** (R1731): the hub SETTINGS panel and the game
// menu, which hands it a `PanelContext` of its own. The markup is the body
// of the section, with no page title, and it draws with the kit of
// `ui/options/` and the classes of `ui/options/cards.css` alone. LIFT takes
// one press, as before: a lift undoes nothing a player cannot redo with
// BLOCK.

import { deleteBlock, getBlocks, type BlockList } from '../../../net/api';
import { hubBlocks as S } from '../../../gen/strings';
import { TTL_ON_OPEN, type Cell } from '../hubstate';
import { esc } from '../../esc';
import { button } from '../../choice/choice';
import { groupHead, note } from '../../options/kit';
import type { PanelContext } from '../panel';
import { emptyNote, errNote, runHeld, type HeldCall } from './optcards';

/** The data cell the card and the CLAN page share: a block from the chat
 *  drops it, so this card reads the new list on its next draw. */
export const BLOCKS_CELL = 'blocks';

/** The block list, through the hub cache. */
export function blocksCell(ctx: Pick<PanelContext, 'data'>): Cell<BlockList> {
  return ctx.data.cell<BlockList>(BLOCKS_CELL, TTL_ON_OPEN, getBlocks);
}

/** The part of a panel the SETTINGS panel mounts. */
export interface BlocksCard {
  html(): string;
  /** Takes one press. Answers false for an action that is not the card's. */
  act(action: string, el: HTMLElement): boolean;
}

/** The first letter of a name, for the round mark of a line. */
function initial(name: string): string {
  const m = /[\p{L}\p{N}]/u.exec(name);
  return m ? m[0].toUpperCase() : '?';
}

/** `heading` writes the group head as an `h3`, the level of the hub
 *  outline (R2048); the game menu (`options/gamehost.ts`) keeps the `div`. */
export function createBlocksCard(ctx: PanelContext, heading = false): BlocksCard {
  const st: HeldCall = { busy: false, err: '' };

  function lift(id: string): void {
    if (st.busy || id === '') return;
    runHeld(ctx, st, 'sfx-menu-press', () => deleteBlock(id), () => { ctx.data.drop(BLOCKS_CELL); });
  }

  return {
    html(): string {
      const read = blocksCell(ctx);
      if (read.state !== 'ready' || read.value === undefined) {
        return groupHead(S.head, S.checking, '', heading) + note(S.lede);
      }
      const list = read.value.blocks;
      const inner = list.length === 0
        ? emptyNote(S.empty, S.empty_why)
        : list.map((b) => `<div class="c-blk"><span class="c-bav" aria-hidden="true">${esc(initial(b.name))}</span>`
          + `<span class="c-bn"><b>${esc(b.name)}</b></span>`
          + button(S.lift, { act: 'block-lift', key: b.id }, { variant: 'primary' }) + '</div>').join('');
      return groupHead(S.head, S.count({ have: list.length, cap: read.value.max }), '', heading)
        + note(S.lede) + inner + errNote(st.err);
    },

    act(action: string, el: HTMLElement): boolean {
      if (action !== 'block-lift') return false;
      lift(el.dataset.key ?? '');
      return true;
    },
  };
}
